import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Edit, GraduationCap, MapPin, MonitorSmartphone, Plus, Search, Trash2, Users } from 'lucide-react';
import { EventListing } from '@/types';
import { eventApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { PriceDisplay } from '@/components/public/PriceDisplay';

type CategoryFilter = 'all' | 'event' | 'workshop';

export const AdminEventsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<EventListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');

  useEffect(() => {
    const controller = new AbortController();

    const fetchItems = async () => {
      try {
        const data = await eventApi.getAllEvents({ signal: controller.signal });
        setItems(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Events are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchItems();
    return () => controller.abort();
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchesCategory = category === 'all' || (item.category || 'event') === category;
      if (!matchesCategory) return false;
      if (!query) return true;

      return [item.title, item.city, item.venue, item.level, ...(item.audience || []), ...(item.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [category, items, search]);

  const handleDelete = async (item: EventListing) => {
    if (!window.confirm(`Are you sure you want to delete this ${item.category === 'workshop' ? 'workshop' : 'event'}?`)) {
      return;
    }

    try {
      await eventApi.deleteEvent(item.id, item.category);
      setItems((prev) => prev.filter((entry) => entry.id !== item.id));
    } catch (deleteError) {
      console.error('Failed to delete event:', deleteError);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="page-heading">Unified Events Module</h1>
          <p className="page-subtitle">
            Manage events and workshops together across one admin workflow, with shared styling, filters, and CRUD.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => navigate('/admin/workshops/new')}>
            <Plus className="h-4 w-4" />
            Add Workshop
          </Button>
          <Button variant="primary" onClick={() => navigate('/admin/events/new')}>
            <Plus className="h-4 w-4" />
            Add Event
          </Button>
        </div>
      </div>

      <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by title, city, level, audience, or tags"
              className="pl-11"
            />
          </div>
          <div>
            <label className="form-label">Category</label>
            <select className="form-control min-h-11" value={category} onChange={(event) => setCategory(event.target.value as CategoryFilter)}>
              <option value="all">All</option>
              <option value="event">Events</option>
              <option value="workshop">Workshops</option>
            </select>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="h-32 animate-pulse rounded-[1.75rem] bg-white/70" />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">{error}</p>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">No events found for the current filters.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item) => {
            const isWorkshop = item.category === 'workshop';
            const editPath = isWorkshop ? `/admin/workshops/${item.id}/edit` : `/admin/events/${item.id}/edit`;

            return (
              <Card key={item.id} className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                    <img src={item.coverImageUrl} alt={item.title} className="h-40 w-full rounded-[1.25rem] object-cover sm:h-24 sm:w-36" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-xl font-semibold text-slate-950">{item.title}</h2>
                        <span className="rounded-full border border-[rgb(var(--accent-solid))/0.16] bg-[rgb(var(--accent-solid))/0.08] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[rgb(var(--accent-solid))]">
                          {isWorkshop ? 'Workshop' : 'Event'}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(item.audience || []).slice(0, 3).map((entry) => (
                          <span key={entry} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                            {entry}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                        <p className="flex items-center gap-2">
                          <CalendarDays className="h-4 w-4" />
                          {item.dateText}
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {item.venue}, {item.city}
                        </p>
                        <p className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4" />
                          {item.level || 'Open to All'}
                        </p>
                        <p className="flex items-center gap-2">
                          <MonitorSmartphone className="h-4 w-4" />
                          {item.teachingMode || 'Offline'}
                        </p>
                        <p className="flex items-center gap-2 sm:col-span-2">
                          <Users className="h-4 w-4" />
                          {item.audience?.length ? item.audience.join(', ') : 'Open to interested participants'}
                        </p>
                        <PriceDisplay isFree={item.isFree} originalPrice={item.priceText} />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 xl:ml-4">
                    <Button variant="secondary" size="sm" onClick={() => navigate(editPath)}>
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(item)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
